import React, { useState, useEffect, useMemo } from "react";
import ApiService, { initialAuthState } from "../Services/ApiServices";
import toast, { Toaster } from "react-hot-toast";
import "./AddressPopupPage.css";

/* ================= VALIDATIONS ================= */
const validatePhone = (phone) => /^[6-9]\d{9}$/.test(phone);
const validatePincode = (pin) => /^[1-9][0-9]{5}$/.test(pin);
const validateName = (name) => name.trim().length >= 2;
const validateAddress = (addr) => addr.trim().length >= 5;

/* ================= COMPONENT ================= */
const AddressPopupPage = ({ address = null, onClose, onSuccess }) => {
  const companyCode = initialAuthState.companyCode;
  const unitCode = initialAuthState.unitCode;

  const [loadingPin, setLoadingPin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isPincodeValid, setIsPincodeValid] = useState(false);
  const [manualLocationEntry, setManualLocationEntry] = useState(false);

  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: "",
    phoneNumber: "",
    addressLineOne: "",
    addressLineTwo: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    addressType: "home",
    isDefault: false,
  });

  /* ================= PREFILL ================= */
  useEffect(() => {
    if (address) {
      setForm({
        name: address.name || "",
        phoneNumber: address.phoneNumber || "",
        addressLineOne: address.addressLineOne || "",
        addressLineTwo: address.addressLineTwo || "",
        landmark: address.landmark || "",
        city: address.city || "",
        state: address.state || "",
        pincode: address.pincode || "",
        country: "India",
        addressType: address.addressType || "home",
        isDefault: address.isDefault || false,
      });

      setIsPincodeValid(true);
    }
  }, [address]);

  /* ================= HANDLE INPUT ================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    const val = type === "checkbox" ? checked : value;

    setForm((prev) => ({ ...prev, [name]: val }));

    let error = "";

    if (name === "name" && value && !validateName(value))
      error = "Name must be at least 2 characters";

    if (name === "phoneNumber" && value && !validatePhone(value))
      error = "Invalid mobile number";

    if (name === "pincode" && value && !validatePincode(value))
      error = "Invalid pincode";

    if (name === "addressLineOne" && value && !validateAddress(value))
      error = "Address is too short";

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  /* ================= PINCODE API ================= */
  // const fetchLocationByPincode = async (pincode) => {
  //   if (!pincode || pincode.length !== 6) return;

  //   if (!validatePincode(pincode)) {
  //     setIsPincodeValid(false);
  //     setErrors((prev) => ({
  //       ...prev,
  //       pincode: "Invalid pincode format",
  //     }));
  //     return;
  //   }

  //   try {
  //     setLoadingPin(true);

  //     const res = await fetch(
  //       `https://api.postalpincode.in/pincode/${pincode}`
  //     );

  //     const data = await res.json();

  //     const isValid =
  //       data?.[0]?.Status === "Success" &&
  //       Array.isArray(data?.[0]?.PostOffice) &&
  //       data[0].PostOffice.length > 0;

  //     if (!isValid) {
  //       setIsPincodeValid(false);
  //       setErrors((prev) => ({
  //         ...prev,
  //         pincode: "Invalid pincode! Enter valid pincode",
  //       }));

  //       toast.dismiss();
  //       toast.error("Invalid pincode");

  //       setIsPincodeValid(false);

  //       return;
  //     }

  //     const office = data[0].PostOffice[0];

  //     setForm((prev) => ({
  //       ...prev,
  //       city: office.District,
  //       state: office.State,
  //     }));

  //     setIsPincodeValid(true);

  //     setErrors((prev) => ({
  //       ...prev,
  //       pincode: "",
  //     }));

  //   } catch (err) {
  //     setIsPincodeValid(false);
  //     // ❗ DO NOT TOUCH EXISTING DATA
  //     setErrors((prev) => ({
  //       ...prev,
  //       pincode: "Unable to verify pincode",
  //     }));
  //     toast.dismiss();
  //     toast.error("Pincode verification failed");
  //   } finally {
  //     setLoadingPin(false);
  //   }
  // };

  const fetchLocationByPincode = async (pincode) => {

    console.log("PINCODE TRIGGERED:", pincode);

    // reset if user deletes value
    if (!pincode || pincode.length < 6) {

      setIsPincodeValid(false);

      setForm((prev) => ({
        ...prev,
        city: "",
        state: "",
      }));

      return;
    }

    // invalid format
    if (!/^[1-9][0-9]{5}$/.test(pincode)) {

      setIsPincodeValid(false);

      setErrors((prev) => ({
        ...prev,
        pincode: "Invalid pincode",
      }));

      return;
    }

    try {

      setLoadingPin(true);

      const response = await ApiService.post(
        "/address/getPincodeDetails",
        { pincode }
      );

      console.log("Response received:", response);

      // ❌ PINCODE NOT FOUND
      if (!response?.status) {

        setIsPincodeValid(false);

        setManualLocationEntry(true);

        setForm((prev) => ({
          ...prev,
          city: "",
          state: "",
        }));

        setErrors((prev) => ({
          ...prev,
          pincode:
            response?.internalMessage ||
            "Pincode not found. Please enter city/state manually.",
        }));

        return;
      }

      // ✅ SUCCESS
      setForm((prev) => ({
        ...prev,
        city: response?.data?.city || "",
        state: response?.data?.state || "",
        country: response?.data?.country || "India",
      }));

      setManualLocationEntry(false);

      setIsPincodeValid(true);

      setErrors((prev) => ({
        ...prev,
        pincode: "",
      }));

    } catch (err) {

      console.error("PINCODE ERROR:", err);

      setIsPincodeValid(false);

      setForm((prev) => ({
        ...prev,
        city: "",
        state: "",
      }));

      setErrors((prev) => ({
        ...prev,
        pincode: "Unable to verify pincode",
      }));

    } finally {

      setLoadingPin(false);
    }
  };

  const isFormValid = useMemo(() => {
    return (
      validateName(form.name) &&
      validatePhone(form.phoneNumber) &&
      validatePincode(form.pincode) &&
      validateAddress(form.addressLineOne) &&
      form.city &&
      form.state &&
      (isPincodeValid || manualLocationEntry)
    );
  }, [form, isPincodeValid]);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!validateName(form.name)) newErrors.name = "Invalid name";
    if (!validatePhone(form.phoneNumber))
      newErrors.phoneNumber = "Invalid phone number";
    if (!validatePincode(form.pincode))
      newErrors.pincode = "Invalid pincode";
    if (!validateAddress(form.addressLineOne))
      newErrors.addressLineOne = "Address too short";
    if (!form.city) newErrors.city = "City required";
    if (!form.state) newErrors.state = "State required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    try {
      setSubmitting(true);

      const payload = {
        ...form,
        companyCode,
        unitCode,
        // clientId: Number(clientId),
      };

      if (address?.id) payload.id = address.id;

      const response = await ApiService.post(
        "/address/handleCreateAddress",
        payload
      );

      if (response.status) {
        toast.dismiss();
        toast.success(
          address ? "Address updated" : "Address saved successfully"
        );
        onSuccess?.();
        onClose?.();
      } else {
        toast.dismiss();
        toast.error(response.internalMessage || "Failed to save address");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(
        err?.response?.data?.internalMessage ||
        err?.message ||
        "Server error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="address-popup-wrapper">
      {/* <Toaster position="top-center" reverseOrder={false} /> */}
      <form className="address-popup-form" onSubmit={handleSubmit}>
        <button
          type="button"
          className="address-popup-close"
          onClick={onClose}
        >
          ✕
        </button>

        <h2 className="address-popup-title">
          {address ? "Update Address" : "Add Delivery Address"}
        </h2>

        <div className="address-grid">

          {/* NAME */}
          <div>
            <input
              name="name"
              placeholder="Full Name"
              value={form.name}
              onChange={handleChange}
              className={`address-popup-input ${errors.name ? "address-input-error" : ""
                }`}
            />
            {errors.name && (
              <span className="address-error-text">{errors.name}</span>
            )}
          </div>

          {/* PHONE */}
          <div>
            <input
              name="phoneNumber"
              placeholder="Mobile Number"
              value={form.phoneNumber}
              onChange={handleChange}
              className={`address-popup-input ${errors.phoneNumber ? "address-input-error" : ""
                }`}
            />
            {errors.phoneNumber && (
              <span className="address-error-text">
                {errors.phoneNumber}
              </span>
            )}
          </div>

          {/* ADDRESS */}
          <div>
            <input
              name="addressLineOne"
              placeholder="House / Flat / Building"
              value={form.addressLineOne}
              onChange={handleChange}
              className={`address-popup-input ${errors.addressLineOne ? "address-input-error" : ""
                }`}
            />
            {errors.addressLineOne && (
              <span className="address-error-text">
                {errors.addressLineOne}
              </span>
            )}
          </div>

          {/* PINCODE */}
          <div>
            <input
              name="pincode"
              placeholder="Pincode"
              maxLength="6"
              value={form.pincode}
              onChange={(e) => {
                handleChange(e);
                fetchLocationByPincode(e.target.value);
              }}
              className={`address-popup-input ${errors.pincode ? "address-input-error" : ""
                }`}
            />
            {loadingPin && (
              <span className="address-info-text">Checking...</span>
            )}
            {errors.pincode && (
              <span className="address-error-text">
                {errors.pincode}
              </span>
            )}
          </div>

          {/* CITY */}
          {/* <input
            name="city"
            placeholder="City"
            value={form.city}
            onChange={handleChange}
            className="address-popup-input"
          /> */}

          {/* STATE */}
          {/* <input
            name="state"
            placeholder="State"
            value={form.state}
            onChange={handleChange}
            className="address-popup-input"
          /> */}

          {/* CITY */}
          <input
            name="city"
            placeholder="City"
            value={form.city}
            onChange={handleChange}
            readOnly={!manualLocationEntry}
            className={`address-popup-input ${!manualLocationEntry
              ? "address-disabled-input"
              : ""
              }`}
          />

          {/* STATE */}
          <input
            name="state"
            placeholder="State"
            value={form.state}
            onChange={handleChange}
            readOnly={!manualLocationEntry}
            className={`address-popup-input ${!manualLocationEntry
              ? "address-disabled-input"
              : ""
              }`}
          />
        </div>

        {/* BUTTON */}
        {/* <div className="address-popup-submit-btn-container">
          <button
            disabled={submitting}
            className="address-popup-submit-btn"
          >
            {submitting
              ? "Saving..."
              : address
                ? "Update Address"
                : "Save Address"}
          </button>
        </div> */}

        <div className="address-popup-submit-btn-container">
          <button
            disabled={!isFormValid || submitting}
            className="address-popup-submit-btn"
            style={{
              opacity: !isFormValid ? 0.5 : 1,
              cursor: !isFormValid ? "not-allowed" : "pointer",
            }}
          >
            {submitting
              ? "Saving..."
              : address
                ? "Update Address"
                : "Save Address"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddressPopupPage;








// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import ApiService, { initialAuthState } from "../Services/ApiServices";
// import "./AddressPopupPage.css";

// const AddressPopupPage = ({ address = null, onClose, onSuccess }) => {
//   const navigate = useNavigate();

//   const [showForm, setShowForm] = useState(!address); // if address is passed, show form directly

//   const companyCode = initialAuthState.companyCode;
//   const unitCode = initialAuthState.unitCode;
//   const clientId = localStorage.getItem("client_id");
//   const dbId = localStorage.getItem("client_db_id");
//   const phoneNumber = localStorage.getItem("client_phone");

//   const [form, setForm] = useState({
//     email: "",
//     subscribe: true,
//     country: "India",
//     firstName: "",
//     lastName: "",
//     company: "",
//     address: "",
//     apartment: "",
//     city: "",
//     state: "Andhra Pradesh",
//     pincode: "",
//     phoneNumber: "",
//     saveInfo: false,
//     addressLineOne: "",
//   });

//   useEffect(() => {
//     if (address) {
//       // Edit mode: pre-fill form with address
//       setForm({
//         email: "",
//         subscribe: true,
//         country: "India",
//         firstName: address.name || "",
//         lastName: "",
//         company: address.company || "",
//         address: address.address || "",
//         apartment: address.apartment || "",
//         city: address.city || "",
//         state: address.state || "Andhra Pradesh",
//         pincode: address.pincode || "",
//         phoneNumber: address.phoneNumber || "",
//         saveInfo: false,
//         addressLineOne: address.addressLineOne || "",
//       });
//     } else {
//       // Add mode: try to fetch existing addresses
//       fetchSavedAddresses();
//     }
//   }, [address]);

//   const fetchSavedAddresses = async () => {
//     try {
//       const payload = { companyCode, unitCode, clientId };
//       const response = await ApiService.post(
//         "/client/getClientDetailsById",
//         payload
//       );
//       if (!response.status) {
//         setShowForm(true);
//       }
//     } catch (error) {
//       console.error("Error fetching addresses:", error);
//       setShowForm(true);
//     }
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setForm((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   const handleRegister = async () => {
//     try {
//       await ApiService.post("/client/handleClientDetails", {
//         companyCode,
//         unitCode,
//         phoneNumber: form.phoneNumber,
//         name: form.firstName,
//         id: dbId,
//         clientId,
//       });
//     } catch (error) {
//       console.error("Registration error:", error);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     handleRegister();

//     try {
//       const payload = {
//         ...form,
//         companyCode,
//         unitCode,
//         clientId: Number(dbId),
//         phoneNumber: form.phoneNumber,
//         name: form.firstName,
//       };

//       if (address?.id) {
//         payload.id = address.id; // important for edit
//       }

//       const response = await ApiService.post(
//         "/address/handleCreateAddress",
//         payload
//       );

//       if (response.status === 200 || response.status) {
//         alert(address ? "Address updated!" : "Address added!");
//         onSuccess?.(); // parent callback
//         onClose?.(); // close popup
//       } else {
//         alert("Failed to save address");
//       }
//     } catch (err) {
//       console.error("Error saving address:", err);
//     }
//   };

//   return (
//     <div className="address-popup-wrapper">
//       <form className="address-form" onSubmit={handleSubmit}>
//         <h2 className="delivery-title">Delivery</h2>

//         <div className="name-fields">
//           <input
//             type="text"
//             name="firstName"
//             placeholder="Name"
//             value={form.firstName}
//             onChange={handleChange}
//             required
//           />
//           {/* <input
//             type="text"
//             name="lastName"
//             placeholder="Last name"
//             value={form.lastName}
//             onChange={handleChange}
//             required
//           /> */}
//         </div>

//         <input
//           type="text"
//           name="company"
//           placeholder="Company (optional)"
//           value={form.company}
//           onChange={handleChange}
//         />
//         <select name="country" value={form.country} onChange={handleChange}>
//           <option value="India">India</option>
//         </select>

//         <div className="location-fields">
//           <input
//             type="text"
//             name="city"
//             placeholder="City"
//             value={form.city}
//             onChange={handleChange}
//             required
//           />
//           <select
//             name="state"
//             value={form.state}
//             onChange={handleChange}
//             required
//           >
//             <option value="Andhra Pradesh">Andhra Pradesh</option>
//             {/* Add more states if needed */}
//           </select>
//           <input
//             type="text"
//             name="pincode"
//             placeholder="PIN code"
//             value={form.pincode}
//             onChange={handleChange}
//             required
//           />
//         </div>

//         <input
//           type="text"
//           name="addressLineOne"
//           placeholder="Address"
//           value={form.addressLineOne}
//           onChange={handleChange}
//           required
//         />

//         <input
//           type="tel"
//           name="phoneNumber"
//           placeholder="Phone Number"
//           value={form.phoneNumber}
//           onChange={handleChange}
//           required
//         />

//         <button type="submit" className="checkout">
//           {address ? "Update Address" : "Continue"}
//         </button>
//       </form>
//     </div>
//   );
// };

// export default AddressPopupPage;
